import { Injectable } from '@angular/core';
import { CacheListener } from './@utils/cache-listener';
import { LoadLocation } from 'interfaces/common/db/models/load-location';
import { Ccolonia, Cmunicipality, CState } from 'interfaces/common/db/models/catalogue';
import { AuthService, NotificationsService } from '@services';
import dexFreight from './microservices';
import { Subscription } from 'rxjs';
import { DataCache } from './@utils/data-cache.class';
import { CacheKeys } from './@enums/CacheKeys.enum';
import { ZipcodeData } from './@enums/ZipcodeData.type';
import type { ParsedLocationsCSV } from 'interfaces/common/lib/modules/locations/parseLocationsCSVFile';
import type { CreateManyLocationsResult } from 'interfaces/common/lib/modules/locations/createManyLocations';
import { Repository } from './@core/classes/Repository.class';
import { DbService } from './@core/db.service';
import debug from "debug";
import { BackgroundTask } from './@core/classes/BackgroundTask.class';
import { dxcMatchesTokens, dxcQueryTokens } from 'src/modules/primitives/dxc';

const log = debug('@load-location');

export interface LoadLocationInfo {
  location: LoadLocation;
  colonia: Ccolonia;
  municipality: Cmunicipality;
  state: CState;
}

@Injectable({
  providedIn: 'root'
})
export class LoadLocationService {

  /**
   * Cuántas filas devuelve una búsqueda como mucho.
   *
   * El listado sin filtro pagina de 20 en 20 con desplazamiento infinito; el
   * filtrado no pagina, así que sin tope una palabra corta —«san»— devolvería el
   * catálogo entero de golpe. Quien busca y recibe 200 filas no ha encontrado
   * nada: lo que necesita es escribir otra palabra, y el listado se lo dice.
   */
  static readonly SEARCH_LIMIT = 200;

  public locations: Repository<LoadLocation>;

  private loadLocations: Record<string, CacheListener<LoadLocationInfo>> = {};
  private listeners: Subscription[] = [];
  public $loadLocations = new CacheListener<LoadLocation[]>('loadLocations', null, this.syncAll.bind(this));
  readonly #zipcodesRange: CacheListener<[number, number][] | null>;
  #syncingLocations = false;

  constructor(
    public auth: AuthService,
    public notifications: NotificationsService,
    public db: DbService
  ) {
    this.#zipcodesRange = new CacheListener("zipcodes-range", DataCache.get<[number, number][]>(CacheKeys.ZipcodesRange) ?? null, this.syncZipcodesRange.bind(this));
    this.locations = new Repository<LoadLocation>({
      db: this.db.locations,
      sync: this.syncLoadLocation.bind(this)
    });
  }

  setupListeners() {
    this.listeners.push(
      this.auth.token.onClear(this.removeListeners.bind(this)),
      this.$loadLocations.syncWhenChanges(this.auth.token),
      this.notifications.notifications.subscribe(notification => {
        if (notification.tags.includes('loadLocationUpdate')) this.processNotification(notification);
      }),
      this.auth.token.subscribe(token => {
        if (token) this.initLoadLocationsTask();
      })
    )
  }

  async locationsPage(page: number) {
    return this.db.locations.offset(page * 20).limit(20).toArray();
  }

  async getById(id: string) {
    return this.db.locations.where('_id').equals(id).first();
  }

  /** Cuántos domicilios tiene la empresa. Se cuenta sobre la copia local, sin red. */
  async count(): Promise<number> {
    return this.db.locations.count();
  }

  /**
   * El domicilio que ya usa ese nombre, si hay uno.
   *
   * El backend localiza un domicilio por su nombre al editarlo y rechaza el alta
   * de uno repetido, pero solo lo dice al guardar y con la palabra «código». Se
   * compara sin distinguir mayúsculas ni espacios de sobra, que es como se cuela
   * un duplicado al capturar a mano.
   */
  async findByName(name: string): Promise<LoadLocation | undefined> {
    const key = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    const wanted = key(name);
    if (!wanted) return undefined;

    return this.db.locations.filter(location => key(location.name) === wanted).first();
  }

  /**
   * Busca por **palabras sueltas** en nombre, dirección, colonia, código postal,
   * ciudad y estado.
   *
   * Antes era `where('name').startsWithIgnoreCase(query)`: solo el nombre, y
   * solo por prefijo. O sea que «Merced» no encontraba «Clinica la Merced»
   * —había que acertar el principio— y no miraba la dirección ni el lugar,
   * aunque las dos sean columnas de la tabla que se está viendo.
   *
   * Buscar dentro del texto **renuncia al índice** de Dexie (`&name` solo sirve
   * para prefijos), así que esto es un barrido. Es asumible porque la tabla es
   * el catálogo de una empresa —decenas o pocos cientos de filas, ya en
   * memoria— y porque `.limit()` corta el recorrido en cuanto llena el tope, no
   * después de recorrerlo entero.
   */
  async search(query: string): Promise<LoadLocation[]> {
    const tokens = dxcQueryTokens(query);

    if (!tokens.length) return [];

    return this.db.locations
      .filter(location => dxcMatchesTokens(
        tokens, location.name, location.address, location.colonia, location.zipcode,
        location.city, location.state,
      ))
      .limit(LoadLocationService.SEARCH_LIMIT)
      .toArray();
  }

  /**
   * Baja el catálogo de ubicaciones de la empresa a Dexie, de 20 en 20.
   *
   * El listado de «Domicilios de origen y destino» lee **solo** de Dexie, así
   * que lo que este barrido no traiga no existe para esa pantalla — aunque el
   * registro esté en la base y otras vistas sí lo enseñen, porque esas piden el
   * catálogo entero (`getLocation`) y no paginan.
   *
   * Dos cosas de las que depende que el barrido llegue al final:
   *
   * **La condición de continuar se mide sobre lo que devolvió el servidor, no
   * sobre lo que se guarda.** Las ubicaciones sin nombre no se escriben —la
   * clave primaria de la tabla es `&name`, así que todas colisionarían en la
   * misma fila—, pero descontarlas antes de comparar contra `batchSize` corta
   * el recorrido en la primera tanda que traiga una. Medido en dev: de 1000
   * ubicaciones, tres no tienen nombre; la primera cae en la página 42, así que
   * el barrido paraba ahí y Dexie se quedaba en 859. Las 141 restantes son las
   * más recientes —el endpoint devuelve orden de inserción—, o sea justo las
   * que se acababan de dar de alta, a mano o por CSV.
   *
   * **La limpieza solo corre si el recorrido terminó.** Borra todo lo que no
   * haya visto, así que sobre un barrido a medias no limpia lo caducado: borra
   * lo que no llegó a mirar. Un fallo de red en una tanda basta, y por eso no
   * alcanza con `syncedLocationIds.size > 0` para darlo por completo.
   */
  async initLoadLocationsTask(batchSize = 20, delayMs = 500) {
    const token = await this.auth.token.get();
    if (!token) return;
    if (this.#syncingLocations) return;
    this.#syncingLocations = true;
    const syncedLocationIds = new Set<string>(); // Use IDs instead of names if available
    let walkedToTheEnd = false;
    log(`Syncing load locations`);

    try {
      await new BackgroundTask(async (index) => {
        try {
          const batch = await dexFreight.LoadLocation.locations(
            index * batchSize,
            batchSize,
            token
          );

          const named = batch.filter(loc => !!loc.name);

          if (named.length > 0) {
            await this.db.locations.bulkPut(named);
            named.forEach(location => syncedLocationIds.add(location._id));
            log(`Synced batch ${index + 1}: ${named.length} of ${batch.length} locations`);
          }

          if (batch.length === batchSize) return true;

          walkedToTheEnd = true;
          return false;
        } catch (err) {
          log(`Error syncing batch ${index}: ${err.message}`);
        }
      }, delayMs).run();

      // Only cleanup if sync completed successfully
      if (walkedToTheEnd && syncedLocationIds.size > 0) {
        const deleteCount = await this.db.locations
          .where('_id').noneOf([...syncedLocationIds]) // Use IDs if available
          .delete();
        log(`Cleaned up ${deleteCount} outdated locations`);
      }

    } catch (err) {
      log(`Load location sync failed: ${err.message}`);
      throw err;
    } finally {
      this.#syncingLocations = false;
    }
  }

  async syncZipcodesRange() {
    const cache = DataCache.get<[number, number][]>(CacheKeys.ZipcodesRange);

    if (cache) return cache;

    const zipcodes = await dexFreight.Locations.getZipcodeRanges();

    DataCache.set(CacheKeys.ZipcodesRange, zipcodes, DataCache.Long);

    return zipcodes;
  }

  async syncAll() {
    const token = await this.auth.token.get();

    if (!token) return null;

    const locations = await dexFreight.LoadLocation.getLocation(token);

    return locations;
  }

  removeListeners() {
    for (const listener of this.listeners) {
      listener.unsubscribe();
    }

    for (const locationName in this.loadLocations) {
      this.loadLocations[locationName].unsubscribe();
      this.loadLocations[locationName].clear();
    }
  }

  processNotification(notification) {
    const { data } = notification;
    const location = this.loadLocations[data.locationName];

    if (location) location.sync();
  }

  location(name: string) {
    const location = this.loadLocations[name];

    if (location) return location;

    this.loadLocations[name] = new CacheListener<LoadLocationInfo>(`loadLocation:${name}`, null, () => this.syncLoadLocation(name));

    return this.loadLocations[name];
  }

  get(name: string) {
    return this.location(name).get();
  }

  async syncLoadLocation(name: string | object) {
    if (typeof name === 'object') {
      return name as LoadLocationInfo;
    }

    try {
      const token = await this.auth.token.get();

      if (!token) return null;

      return await dexFreight.LoadLocation.get(name, token);
    } catch (exc) {
      if (exc.code === 'LOAD_LOCATION_CODE_NOT_FOUND') return null;

      return null;
    }
  }

  async existingZipcodes(prefix: string): Promise<string[]> {
    const ranges: [number, number][] = (await this.#zipcodesRange.get()) ?? [];
    const out: string[] = [];

    // generic power-of-10 so we don’t hard-code 5-digit ZIPs
    const digitsLeft = Math.max(0, 5 - prefix.length);
    const lower = +prefix.padEnd(prefix.length + digitsLeft, '0');
    const upper = +prefix.padEnd(prefix.length + digitsLeft, '9');

    for (const [from, to] of ranges) {
      if (to < lower) continue;          // range ends before our window
      if (from > upper) break;           // range starts after our window -> done

      const start = Math.max(from, lower);
      const end = Math.min(to, upper);

      for (let zip = start; zip <= end && out.length < 10; zip++) {
        out.push(zip.toString().padStart(5, '0')); // keep leading zeros
      }
      if (out.length >= 10) break;
    }
    return out;
  }

  async getZipcodeData(zipcode: string) {
    const cache = await this.db.zipcodeData.get(zipcode);
    if (cache) return cache.data;

    const result = await dexFreight.Locations.getZipcodeData(zipcode) as ZipcodeData;

    await this.db.zipcodeData.put({ zipcode, data: result });

    return result;
  }

  /**
   * Vuelve a pedir el catálogo completo al servidor.
   *
   * Se llama tras CADA escritura, y va en el servicio y no en las vistas por la
   * razón de siempre: un «acuérdate de sincronizar también» se cumple a medias.
   * Se cumplía, de hecho: las dos únicas llamadas a `$loadLocations.sync()` que
   * había vivían en el flujo de fletes —guardar un borrador y editar un
   * embarque, que hacen `upsert` de ubicaciones en el servidor— y ninguna en la
   * pantalla de Ubicaciones, que es la que existe para crearlas.
   *
   * El efecto medido: `$loadLocations` es un `CacheListener` que solo se
   * resincroniza cuando cambia el token, así que alimenta el desplegable de
   * creación de flete con la foto del arranque de la sesión. Las ubicaciones
   * dadas de alta después —a mano o por CSV— no aparecían ahí hasta recargar la
   * página, mientras que el listado de Ubicaciones sí las mostraba porque lee de
   * Dexie. La misma ubicación existía en una pantalla y no en la otra.
   *
   * No propaga: si la resincronización falla, el alta ya ocurrió y decirle al
   * usuario que no se guardó sería mentirle. El siguiente arranque lo corrige.
   *
   * `CacheListener.sync()` colapsa las llamadas concurrentes en una sola —
   * devuelve la promesa en vuelo si ya hay una—, que es lo que permite al alta
   * por lote pedirla en cada tanda sin traerse el catálogo entero N veces.
   */
  private async refreshLocationsCache() {
    try {
      await this.$loadLocations.sync();
    } catch (exc) {
      log(`Could not refresh the load locations cache: ${exc?.message}`);
    }
  }

  async removeLocation(locationId: string) {
    await dexFreight.LoadLocation.deleteLoadId(locationId, localStorage.token);
    await this.db.locations.where('_id').equals(locationId).delete();
    await this.refreshLocationsCache();
  }

  async updateLocation(_id: string, location: LoadLocation) {
    await dexFreight.LoadLocation.update(location, localStorage.token);
    await this.db.locations.put({
      ...location,
      _id
    });
    await this.refreshLocationsCache();
  }

  async createLocation(location: LoadLocation) {
    await dexFreight.LoadLocation.create(location, localStorage.token);
    await this.db.locations.put(location);
    await this.refreshLocationsCache();
  }

  // ── Carga masiva por CSV ────────────────────────────────────────────────
  //
  // El parseo del CSV vive en el backend, como el resto del producto: el
  // cliente lee el archivo como texto y lo manda. Así hay un solo tokenizador
  // de CSV en dexCarga y no entra una segunda librería en el navegador.

  /** Lee el archivo y devuelve sus filas mapeadas. No escribe nada. */
  async parseLocationsCSV(content: string): Promise<ParsedLocationsCSV> {
    return await dexFreight.LoadLocation.parseLocationsCSV(content, localStorage.token) as ParsedLocationsCSV;
  }

  /**
   * Resuelve el catálogo de un conjunto de códigos postales.
   *
   * Va de ocho en ocho y avisa a cada uno: **es el tramo que alimenta la barra
   * de progreso de la validación**, y por eso la barra puede ser determinada en
   * vez de un giro decorativo — se sabe cuántos faltan. `getZipcodeData` ya
   * cachea en Dexie sin caducidad, así que reimportar el mismo archivo no hace
   * una sola llamada.
   */
  async resolveZipcodes(
    zipcodes: string[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<Map<string, ZipcodeData | null>> {
    const unique  = [...new Set(zipcodes.filter(Boolean))];
    const results = new Map<string, ZipcodeData | null>();
    let done = 0;

    onProgress?.(0, unique.length);

    for (let index = 0; index < unique.length; index += 8) {
      await Promise.all(unique.slice(index, index + 8).map(async zipcode => {
        try {
          results.set(zipcode, await this.getZipcodeData(zipcode));
        } catch {
          // Un CP que el catálogo no reconoce no es un fallo de la importación:
          // es un dato malo de UNA fila, y así lo cuenta el validador.
          results.set(zipcode, null);
        } finally {
          onProgress?.(++done, unique.length);
        }
      }));
    }

    return results;
  }

  /** Los rangos de códigos postales del SAT, cacheados. */
  async zipcodeRanges(): Promise<[number, number][]> {
    return (await this.#zipcodesRange.get()) ?? [];
  }

  /**
   * Las ubicaciones ya registradas. Se devuelven **enteras y no solo sus
   * nombres**: al detectar una repetida hay que poder enseñar cuál es la que ya
   * está, campo a campo, y con una lista de cadenas eso obligaría a una segunda
   * consulta por cada fila repetida.
   */
  async allLocations(): Promise<LoadLocation[]> {
    const locations = await this.db.locations.toArray();
    return locations.filter(location => !!location.name);
  }

  /**
   * Alta por lote. **Nunca sobrescribe**: un nombre que ya existe se aparta en
   * el servidor, y el llamador ya excluye esas filas antes de mandarlas.
   *
   * Escribe también en la caché local **con el `_id` que devuelve el
   * servidor**: `createLocation` guarda sin él, así que la fila recién creada
   * se queda sin identidad y sus botones de editar y borrar no tienen a qué
   * apuntar hasta la siguiente sincronización.
   */
  async createManyLoadLocations(locations: LoadLocation[]): Promise<CreateManyLocationsResult> {
    const result = await dexFreight.LoadLocation.createManyLoadLocations(
      locations as any, localStorage.token,
    ) as CreateManyLocationsResult;

    if (result?.locations?.length) {
      await this.db.locations.bulkPut(result.locations as unknown as LoadLocation[]);

      // Sin `await`, y a propósito: esto se llama una vez por tanda de 25, y
      // esperar aquí encadenaría un catálogo completo por tanda contra la barra
      // de progreso. Los consumidores son reactivos —`information-mx` está
      // suscrito con `onValue`—, así que un valor que llega tarde igual repinta
      // el desplegable. Y como `sync()` colapsa lo concurrente, las tandas de un
      // archivo grande se resuelven en una o dos llamadas, no en una por tanda.
      void this.refreshLocationsCache();
    }

    return result;
  }
}
